import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Skeleton } from '../../components/ui/skeleton';
import { Switch } from '../../components/ui/switch';
import { Badge } from '../../components/ui/badge';
import { entitlementsAPI } from '../../lib/api';
import { Shield, Save } from 'lucide-react';
import { toast } from 'sonner';

const FIELD_DEFS = [
  { key: 'metered_views_per_month', label: 'Free articles / month (-1 = unlimited)', type: 'number' },
  { key: 'max_bookmarks',           label: 'Bookmarks limit (-1 = unlimited)',       type: 'number' },
  { key: 'offline_articles',        label: 'Offline articles cap (-1 = unlimited)', type: 'number' },
  { key: 'premium_articles',        label: 'Premium content access',                type: 'bool' },
  { key: 'newsletter',              label: 'Newsletter access',                     type: 'bool' },
  { key: 'comments',                label: 'Comments enabled',                      type: 'bool' },
  { key: 'api_access',              label: 'API access',                            type: 'bool' },
];

export default function AdminEntitlements() {
  const [tiers, setTiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingTier, setSavingTier] = useState(null);
  const [drafts, setDrafts] = useState({});

  const refresh = () => {
    setLoading(true);
    entitlementsAPI
      .list()
      .then((r) => {
        const list = r.data?.entitlements || [];
        setTiers(list);
        const d = {};
        list.forEach((t) => { d[t.tier] = { ...t }; });
        setDrafts(d);
      })
      .catch(() => toast.error('Failed to load entitlements'))
      .finally(() => setLoading(false));
  };

  useEffect(refresh, []);

  const setField = (tier, field, value) => {
    setDrafts((prev) => ({ ...prev, [tier]: { ...prev[tier], [field]: value } }));
  };

  const save = async (tier) => {
    setSavingTier(tier);
    try {
      await entitlementsAPI.update(tier, drafts[tier]);
      toast.success(`${tier} entitlements saved`);
      refresh();
    } catch {
      toast.error('Save failed');
    } finally { setSavingTier(null); }
  };

  return (
    <div className="space-y-6" data-testid="admin-entitlements-page">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2"><Shield className="w-6 h-6 text-[#B89B5E]" /> Entitlements</h1>
        <p className="text-sm text-[#5B616E] mt-1">Configure what each subscription tier unlocks. Applies in real time to the metered paywall and feature gates.</p>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">{[1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-72 rounded-xl" />)}</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
          {tiers.map((t) => {
            const draft = drafts[t.tier] || t;
            return (
              <Card key={t.tier} className="bg-white border-[#E7E0D6]" data-testid={`entitlements-${t.tier}`}>
                <CardHeader className="pb-2 flex flex-row items-center justify-between">
                  <CardTitle className="text-sm capitalize flex items-center gap-2">
                    {t.tier}
                    <Badge variant="outline" className="text-[10px]">{draft.label || t.tier}</Badge>
                  </CardTitle>
                  <Button
                    size="sm"
                    className="bg-[#121316] text-white hover:bg-[#1B1D22] h-8"
                    disabled={savingTier === t.tier}
                    onClick={() => save(t.tier)}
                  >
                    <Save className="w-3 h-3 mr-1" />Save
                  </Button>
                </CardHeader>
                <CardContent className="space-y-3">
                  {FIELD_DEFS.map((field) => (
                    <div key={field.key} className="space-y-1">
                      <label className="text-[10px] text-[#5B616E] block">{field.label}</label>
                      {field.type === 'bool' ? (
                        <Switch
                          checked={!!draft[field.key]}
                          onCheckedChange={(v) => setField(t.tier, field.key, v)}
                        />
                      ) : (
                        <Input
                          type="number"
                          value={draft[field.key] ?? 0}
                          onChange={(e) => setField(t.tier, field.key, parseInt(e.target.value, 10))}
                          className="h-8 text-xs"
                        />
                      )}
                    </div>
                  ))}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
